//
// DISCLAIMER
//
// Copyright 2019-2026 ArangoDB GmbH, Cologne, Germany
//

import { numberFormat } from "humanize";
import moment from "moment";
import React, { Component } from "react";
import { RouteComponentProps } from "react-router-dom";
import { Loader, Table, Popup, Label, SemanticCOLORS } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import {
  ListUsageItemsRequest as ApiListUsageItemsRequest,
  UsageItem_BackupStorageSize as ApiUsageItemBackupStorageSize,
  UsageItem_DeploymentSize as ApiUsageItemDeploymentSize,
  UsageItem_NetworkTransferSize as ApiUsageItemNetworkTransferSize,
  UsageItem_AuditLogSize as ApiUsageItemAuditLogSize,
  UsageItem_AuditLogStorageSize as ApiUsageItemAuditLogStorageSize,
  UsageItem_NotebookSize as ApiUsageItemNotebookSize,
  UsageItem_Resource as ApiUsageItemResource,
  ListAllUsageItemsRequest as ApiListAllUsageItemsRequest,
  UsageItemInfo as ApiUsageItemInfo,
  Money,
  UsageItemInfoList as ApiUsageItemInfoList,
  UsageItem_CPUHour as ApiUsageItemCPUHour,
  UsageItem_MemoryHour as ApiUsageItemMemoryHour,
  UsageItem_StorageHour as ApiUsageItemStorageHour,
  UsageItem_StoragePerformanceHour as ApiUsageItemStoragePerformanceHour,
  UsageItem_GPUHour as ApiUsageItemGPUHour,
  UsageItem_NetworkSize as ApiUsageItemNetworkSize,
  UsageItem_CloudStorageHour as ApiUsageItemCloudStorageHour,
  UsageItem_DeploymentAEU as ApiUsageItemDeploymentAEU,
  UsageItem_AuditLogRequests as ApiUsageItemAuditLogRequests,
  UsageItem_AuditLogData as ApiUsageItemAuditLogData,
  UsageItem_AddonHour as ApiUsageItemAddonHour,
  UsageItemIngestionStatus as ApiUsageItemIngestionStatus,
} from "../../api/lib";
import { ContentSegment, ErrorMessage, LoaderBoxForTable as LoaderBox, Loading, PagingButtons, TextLink, momentNow } from "../../ui/lib";
import { Routes } from "../../routes";
import { IWithRefreshProps, withRefresh } from "../../util/WithRefresh";
import InvoiceLink from "../billing/InvoiceLink";
import ReactJson from "react-json-view";
import {
  DateTimeInUTC,
  DateTimeTransformationTypes,
  FormattedDateWithLabel,
  FormattedElapsedTimeWithLabel,
  HumanisedDuration,
  HUMANIZED_DATE_FORMAT,
} from "../../util/dateAndTimeUtils/DateTime";
import { humanizeFileSize } from "../../util/FileSize";
import UsageItemFilterView, { isV2OnlyKind } from "./UsageItemFilterView";
import UsageCreditsSummary from "./UsageCreditsSummary";

// Arguments for header view
interface IHeaderView {
  loading: boolean;
  page: number;
  pageSize: number;
  count: number;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  selectedKind: string;
  onSelectKind: (kind: string) => void;
}

// Exported so UsageCreditsSummary (which sums the same per-row credit value
// this file's own Credits column shows - see RowView below) can convert
// Money the same way, instead of a second converter drifting from this one.
export const moneyAsNumber = (m?: Money): number => {
  if (!m) {
    return 0;
  }
  return Number(m.units || 0) + (m.nanos || 0) / 1e9;
};

// The API returns enum values as strings in JSON, while the generated TS enum is numeric.
const normalizeIngestionStatus = (status?: ApiUsageItemIngestionStatus | string | null): ApiUsageItemIngestionStatus | undefined => {
  if (status == null || status === "") {
    return ApiUsageItemIngestionStatus.USAGE_ITEM_INGESTION_STATUS_NOT_INGESTED;
  }
  if (typeof status === "number") {
    return status;
  }
  const statusFromEnumName = ApiUsageItemIngestionStatus[status as keyof typeof ApiUsageItemIngestionStatus];
  if (typeof statusFromEnumName === "number") {
    return statusFromEnumName;
  }
  const numericStatus = Number(status);
  if (Number.isFinite(numericStatus)) {
    return numericStatus;
  }
  return undefined;
};

// Maps a Billing 2.0 ingestion status to a label color + text.
const ingestionStatusLabel = (status?: ApiUsageItemIngestionStatus | string | null): { color: SemanticCOLORS; text: string } | undefined => {
  switch (normalizeIngestionStatus(status)) {
    case ApiUsageItemIngestionStatus.USAGE_ITEM_INGESTION_STATUS_NOT_INGESTED:
      return { color: "grey", text: "Not ingested" };
    case ApiUsageItemIngestionStatus.USAGE_ITEM_INGESTION_STATUS_PENDING:
      return { color: "yellow", text: "Ingested, waiting for status" };
    case ApiUsageItemIngestionStatus.USAGE_ITEM_INGESTION_STATUS_INTEGRATED:
      return { color: "green", text: "Integrated" };
    case ApiUsageItemIngestionStatus.USAGE_ITEM_INGESTION_STATUS_FAILED:
      return { color: "red", text: "Failed" };
    case ApiUsageItemIngestionStatus.USAGE_ITEM_INGESTION_STATUS_SKIPPED:
      return { color: "grey", text: "Skipped" };
    case ApiUsageItemIngestionStatus.USAGE_ITEM_INGESTION_STATUS_NOT_BILLABLE:
      return { color: "grey", text: "Not billable" };
    default:
      return undefined;
  }
};

const deprecatedUsageItemKinds = new Set([
  "NetworkTransferSize",
  "DeploymentSize",
  "BackupStorageSize",
  "AuditLogSize",
  "AuditLogStorageSize",
  "NotebookSize",
  "MLServicesSize",
  "MLJobSize",
  "GraphAnalyticsJobSize",
]);

// Exported for UsageCreditsSummary, which groups its per-component totals by
// this same label so a "(deprecated) ..." kind reads the same way in the
// summary as it does in this table's Type column.
export const formatUsageItemKind = (kind?: string): string => {
  if (!kind) {
    return "";
  }
  if (deprecatedUsageItemKinds.has(kind)) {
    return `(deprecated) ${kind}`;
  }
  return kind;
};

// The filter fields reloadUsageItems below turns into a ListAllUsageItemsRequest.
// Exported so UsageCreditsSummary builds its own request with exactly the same
// filter semantics (which fields get omitted-when-unset, what "all"/"allV2" mean,
// etc.) instead of a second, hand-copied version that could silently drift from
// this one and sum a different set of items than the table below is showing.
export interface UsageItemsRequestArgs {
  organization_id?: string;
  project_id?: string;
  deploymentID?: string;
  from: Date;
  to: Date;
  selectedKind: string;
  invoiceID?: string;
  hideEmpty: boolean;
  page: number;
  pageSize: number;
}

export const buildUsageItemsRequest = (args: UsageItemsRequestArgs): ApiListAllUsageItemsRequest => {
  const ireq: ApiListUsageItemsRequest = {
    options: {
      page: args.page,
      page_size: args.pageSize,
    },
    from: args.from,
    to: args.to,
    sort_descending: true,
  };
  if (args.organization_id) {
    ireq.organization_id = args.organization_id;
  }
  if (args.project_id) {
    ireq.project_id = args.project_id;
  }
  if (args.deploymentID) {
    ireq.deployment_id = args.deploymentID;
  }
  // "all" and "allV2" do not set a single-kind server filter. "allV2" is handled
  // server-side via only_billing_v2 below (correct across pages), not client-side.
  if (args.selectedKind !== "all" && args.selectedKind !== "allV2") {
    ireq.kind = args.selectedKind;
  }
  if (args.invoiceID) {
    ireq.invoice_id = args.invoiceID;
  }
  return {
    request: ireq,
    // Gated on isV2OnlyKind, not just args.hideEmpty: the server's
    // exclude_empty filter only checks Billing 2.0 quantity leaves (see
    // isV2OnlyKind's own comment in UsageItemFilterView.tsx), so applying it
    // for "all" or a single legacy kind would silently hide real, non-empty
    // V1 items. UsageItemFilterView only shows the checkbox for a V2-only
    // kind selection, but this is the authoritative gate: it holds even if
    // `hideEmpty` is left over true in state from a kind that no longer
    // qualifies (e.g. the viewer had it checked under "All V2" and then
    // switched to "All").
    exclude_empty: args.hideEmpty && isV2OnlyKind(args.selectedKind),
    only_billing_v2: args.selectedKind === "allV2",
  };
};

const HeaderView = ({ ...args }: IHeaderView) => {
  return (
    <Table.Header>
      <Table.Row>
        <Table.HeaderCell>Type</Table.HeaderCell>
        <Table.HeaderCell>Resource ID</Table.HeaderCell>
        <Table.HeaderCell>Tier</Table.HeaderCell>
        <Table.HeaderCell>Location</Table.HeaderCell>
        <Table.HeaderCell>Usage</Table.HeaderCell>
        <Table.HeaderCell>Start</Table.HeaderCell>
        <Table.HeaderCell>End</Table.HeaderCell>
        <Table.HeaderCell>Duration</Table.HeaderCell>
        <Table.HeaderCell>Invoice</Table.HeaderCell>
        <Table.HeaderCell>CreditBundles</Table.HeaderCell>
        <Table.HeaderCell>Price</Table.HeaderCell>
        <Table.HeaderCell>Credits</Table.HeaderCell>
        <Table.HeaderCell>Ingestion</Table.HeaderCell>
        <Table.HeaderCell>
          <PagingButtons {...args} size="small" />
          <LoaderBox>
            <Loader size="mini" active={args.loading} inline />
          </LoaderBox>
        </Table.HeaderCell>
      </Table.Row>
    </Table.Header>
  );
};

interface IResourceViewArgs {
  resource?: ApiUsageItemResource;
}

// The column shows the identifier of the anchor the usage item is charged on,
// linked to its detail page. Always the identifier, never the name: not every
// producer fills out the (deprecated) name fields - dedicated data cluster
// AddonHour items, for one, are built from Prometheus labels, which carry ids
// only - so a name-first view showed some rows by name and others by id. The
// name, when known, is offered as a tooltip instead.
const ResourceView = ({ ...args }: IResourceViewArgs) => {
  const res = args.resource || {};
  switch (res.kind) {
    // Cluster-level usage (e.g. dedicated data cluster AddonHour items) is anchored
    // on the data cluster, not on a deployment.
    case "DataCluster": {
      const id = res.id || "";
      return (
        <span>
          Data cluster: <TextLink label={id} href={Routes.dashboard_support_datacluster_detailsWithId(id)} />
        </span>
      );
    }
    // Platform-level audit log usage is anchored on the organization itself.
    case "Organization": {
      const id = res.id || "";
      return (
        <span title={res.organization_name || undefined}>
          Organization: <TextLink label={id} href={Routes.dashboard_sales_organization_detailsWithId(id)} />
        </span>
      );
    }
    case "Deployment": {
      const id = res.deployment_id || res.id || "";
      return (
        <span title={res.deployment_name || undefined}>
          Deployment: <TextLink label={id} href={Routes.dashboard_support_deployment_detailsWithId(id)} />
        </span>
      );
    }
    default:
      return <span>{res.kind ? `${res.kind}: ${res.id || ""}` : ""}</span>;
  }
};

interface IDeploymentSizeView {
  item: ApiUsageItemDeploymentSize;
}

const DeploymentSizeView = ({ ...args }: IDeploymentSizeView) => {
  const item = args.item;
  const totalMem =
    (item.agent_memory_size || 0) * (item.agents || 0) +
    (item.coordinator_memory_size || 0) * (item.coordinators || 0) +
    (item.dbserver_memory_size || 0) * (item.dbservers || 0);
  const totalDisk = (item.agent_disk_size || 0) * (item.agents || 0) + (item.dbserver_disk_size || 0) * (item.dbservers || 0);
  return (
    <span>
      {totalMem} GiB RAM, {totalDisk} GiB disk
    </span>
  );
};

interface INetworkTransferSizeView {
  item: ApiUsageItemNetworkTransferSize;
}

const NetworkTransferSizeView = ({ ...args }: INetworkTransferSizeView) => {
  const item = args.item;
  return (
    <span>
      Destination: {item.destination}, Ingress: {humanizeFileSize(item.total_transfer_ingress_size || 0)}, Egress:
      {humanizeFileSize(item.total_transfer_egress_size || 0)}
    </span>
  );
};

interface IBackupStorageSizeView {
  item: ApiUsageItemBackupStorageSize;
}

const BackupStorageSizeView = ({ ...args }: IBackupStorageSizeView) => {
  const item = args.item;
  return <span>{humanizeFileSize(item.cloud_storage_size || 0)}</span>;
};

interface IAuditLogSizeView {
  item: ApiUsageItemAuditLogSize;
}

const AuditLogSizeView = ({ ...args }: IAuditLogSizeView) => {
  const item = args.item;
  return (
    <span>
      DestinationType: {item.destination_type}, EventCount: {numberFormat(item.event_count || 0, 0)}, EventSize: {humanizeFileSize(item.event_size || 0)},
      HttpsPostCount: {numberFormat(item.https_post_count || 0, 0)}
    </span>
  );
};

interface IAuditLogStorageSizeView {
  item: ApiUsageItemAuditLogStorageSize;
}

const AuditLogStorageSizeView = ({ ...args }: IAuditLogStorageSizeView) => {
  const item = args.item;
  return <span>CloudStorageSize: {humanizeFileSize(item.cloud_storage_size || 0)}</span>;
};

interface INotebookSizeView {
  item: ApiUsageItemNotebookSize;
}

interface ICPUHourView {
  item: ApiUsageItemCPUHour;
}

const CPUHourView = ({ ...args }: ICPUHourView) => {
  const item = args.item;
  return <span>CPU Hours: {numberFormat(item.cpu_hours || 0, 4)}</span>;
};

interface IMemoryHourView {
  item: ApiUsageItemMemoryHour;
}

const MemoryHourView = ({ ...args }: IMemoryHourView) => {
  const item = args.item;
  return <span>Memory GiB Hours: {numberFormat(item.memory_gib_hours || 0, 4)}</span>;
};

interface IStorageHourView {
  item: ApiUsageItemStorageHour;
}

const StorageHourView = ({ ...args }: IStorageHourView) => {
  const item = args.item;
  return <span>Storage GiB Hours: {numberFormat(item.storage_gib_hours || 0, 4)}</span>;
};

interface IStoragePerformanceHourView {
  item: ApiUsageItemStoragePerformanceHour;
}

const StoragePerformanceHourView = ({ ...args }: IStoragePerformanceHourView) => {
  const item = args.item;
  return <span>Storage Performance Unit Hours: {numberFormat(item.storage_performance_unit_hours || 0, 4)}</span>;
};

interface IGPUHourView {
  item: ApiUsageItemGPUHour;
}

const GPUHourView = ({ ...args }: IGPUHourView) => {
  const item = args.item;
  return <span>GPU Hours: {numberFormat(item.gpu_hours || 0, 4)}</span>;
};

interface INetworkSizeView {
  item: ApiUsageItemNetworkSize;
}

const NetworkSizeView = ({ ...args }: INetworkSizeView) => {
  const item = args.item;
  return (
    <span>
      Destination: {item.destination}, Ingress: {humanizeFileSize(item.total_transfer_ingress_size || 0)}, Egress:{" "}
      {humanizeFileSize(item.total_transfer_egress_size || 0)}
    </span>
  );
};

interface ICloudStorageHourView {
  item: ApiUsageItemCloudStorageHour;
}

const CloudStorageHourView = ({ ...args }: ICloudStorageHourView) => {
  const item = args.item;
  const purposeBreakdown = item.purpose_breakdown || "";
  return (
    <span>
      GiB Hours: {numberFormat(item.gib_hours || 0, 4)}, Average Size: {humanizeFileSize(item.cloud_storage_size || 0)}
      {purposeBreakdown && ` (${purposeBreakdown})`}
    </span>
  );
};

interface IDeploymentAEUView {
  item: ApiUsageItemDeploymentAEU;
}

const DeploymentAEUView = ({ ...args }: IDeploymentAEUView) => {
  const item = args.item;
  return <span>Deployment AEU Hours: {numberFormat(item.deployment_aeu_hours || 0, 4)}</span>;
};

interface IAuditLogRequestsView {
  item: ApiUsageItemAuditLogRequests;
}

const AuditLogRequestsView = ({ ...args }: IAuditLogRequestsView) => {
  const item = args.item;
  return <span>Requests: {numberFormat(item.count || 0, 0)}</span>;
};

interface IAuditLogDataView {
  item: ApiUsageItemAuditLogData;
}

const AuditLogDataView = ({ ...args }: IAuditLogDataView) => {
  const item = args.item;
  return <span>Data: {humanizeFileSize(item.bytes || 0)}</span>;
};

interface IAddonHourView {
  item: ApiUsageItemAddonHour;
}

// The addon id is the sub-category of the generic AddonHour kind (e.g.
// "dedicateddatacluster"), so show it first.
const AddonHourView = ({ ...args }: IAddonHourView) => {
  const item = args.item;
  return (
    <span>
      Addon: {item.addon_id || "?"}, Hours: {numberFormat(item.addon_hours || 0, 4)}
    </span>
  );
};

const NotebookSizeView = ({ ...args }: INotebookSizeView) => {
  const item = args.item;
  let gpu = "";
  if (item.gpu_size) {
    gpu = `, Gpu: ${numberFormat(item.gpu_size || 0, 0)}`;
  }
  if (item.is_paused) {
    return (
      <span>
        Model: {item.notebook_model_id}, Disk: {numberFormat(item.disk_size || 0, 0)} Gib, Paused
      </span>
    );
  }
  return (
    <span>
      Model: {item.notebook_model_id}, Disk: {numberFormat(item.disk_size || 0, 0)} Gib, Memory: {numberFormat(item.memory_size || 0)} Gib, Cpu:{" "}
      {numberFormat(item.cpu_size || 0, 0)}
      {gpu}
    </span>
  );
};

// Interface describing a usage item
interface IRowView extends IWithRefreshProps {
  itemInfo: ApiUsageItemInfo;
  onInvoiceSelected: (id: string) => void;
}

const RowView = ({ ...args }: IRowView) => {
  const item = args.itemInfo.usage_item || {};
  const kind = formatUsageItemKind(item.kind);
  const hasPrice = !!args.itemInfo.price;
  const price = args.itemInfo.price || {};
  const hasCredits = !!args.itemInfo.credits;
  const credits = args.itemInfo.credits || {};
  const hasCost = !!args.itemInfo.cost;
  const cost = args.itemInfo.cost || {};
  const ingestion = ingestionStatusLabel(args.itemInfo.ingestion_status);
  const duration = item.has_ended ? moment(item.ends_at).diff(moment(item.starts_at)) : momentNow().diff(moment(item.starts_at));

  return (
    <Table.Row>
      <Table.Cell>
        <Popup trigger={<u>{kind}</u>} content={<ReactJson src={args.itemInfo} collapsed={1} />} on="click" pinned wide="very" position="right center" />
      </Table.Cell>
      <Table.Cell>
        <ResourceView resource={item.resource} />
      </Table.Cell>
      <Table.Cell>{item.tier_id || ""}</Table.Cell>
      <Table.Cell>
        {(item.resource || {}).cloud_provider_id} / {(item.resource || {}).cloud_region_id}
      </Table.Cell>
      <Table.Cell>
        {item.deployment_size && <DeploymentSizeView item={item.deployment_size} />}
        {item.network_transfer_size && <NetworkTransferSizeView item={item.network_transfer_size} />}
        {item.backup_storage_size && <BackupStorageSizeView item={item.backup_storage_size} />}
        {item.auditlog_size && <AuditLogSizeView item={item.auditlog_size} />}
        {item.auditlog_storage_size && <AuditLogStorageSizeView item={item.auditlog_storage_size} />}
        {item.notebook_size && <NotebookSizeView item={item.notebook_size} />}
        {item.cpu_hour && <CPUHourView item={item.cpu_hour} />}
        {item.memory_hour && <MemoryHourView item={item.memory_hour} />}
        {item.storage_hour && <StorageHourView item={item.storage_hour} />}
        {item.storage_performance_hour && <StoragePerformanceHourView item={item.storage_performance_hour} />}
        {item.gpu_hour && <GPUHourView item={item.gpu_hour} />}
        {item.network_size && <NetworkSizeView item={item.network_size} />}
        {item.cloud_storage_hour && <CloudStorageHourView item={item.cloud_storage_hour} />}
        {item.deployment_aeu && <DeploymentAEUView item={item.deployment_aeu} />}
        {item.audit_log_requests && <AuditLogRequestsView item={item.audit_log_requests} />}
        {item.audit_log_data && <AuditLogDataView item={item.audit_log_data} />}
        {item.addon_hour && <AddonHourView item={item.addon_hour} />}
      </Table.Cell>
      <Table.Cell>
        <Popup
          size="mini"
          wide="very"
          content={
            <>
              <FormattedDateWithLabel label="Start" dateTime={item.starts_at} transformTo={DateTimeTransformationTypes.LOCAL} />
              <FormattedElapsedTimeWithLabel label="Start" dateTime={item.starts_at} />
            </>
          }
          trigger={
            <span>
              <DateTimeInUTC dateTime={item.starts_at || ""} format={HUMANIZED_DATE_FORMAT} />
            </span>
          }
        />
      </Table.Cell>
      <Table.Cell>
        {item.has_ended ? (
          <Popup
            size="mini"
            wide="very"
            content={
              <>
                <FormattedDateWithLabel label="End" dateTime={item.ends_at} transformTo={DateTimeTransformationTypes.LOCAL} />
                <FormattedElapsedTimeWithLabel label="End" dateTime={item.ends_at} />
              </>
            }
            trigger={
              <span>
                <DateTimeInUTC dateTime={item.ends_at || ""} format={HUMANIZED_DATE_FORMAT} />
              </span>
            }
          />
        ) : (
          "-"
        )}
      </Table.Cell>
      <Table.Cell>
        <HumanisedDuration duration={duration} />
      </Table.Cell>
      <Table.Cell>
        <InvoiceLink {...args} invoice_id={item.invoice_id} />
      </Table.Cell>
      <Table.Cell>
        {((item.resource || {}).credit_bundle_ids || []).map((id) => {
          return <div>{id}</div>;
        })}
      </Table.Cell>
      <Table.Cell>
        {hasPrice ? (
          <Popup
            trigger={<u>{numberFormat(moneyAsNumber(price.total))}</u>}
            content={<ReactJson src={price} />}
            on="click"
            pinned
            wide="very"
            position="left center"
          />
        ) : hasCost && !!cost.amount ? (
          <Popup
            trigger={<u>{numberFormat(moneyAsNumber(cost.amount))}</u>}
            content={<ReactJson src={cost} />}
            on="click"
            pinned
            wide="very"
            position="left center"
          />
        ) : (
          <span>n/a</span>
        )}
      </Table.Cell>
      <Table.Cell>
        {hasCredits ? (
          <Popup
            trigger={<u>{numberFormat(moneyAsNumber(credits.total))}</u>}
            content={<ReactJson src={credits} />}
            on="click"
            pinned
            wide="very"
            position="left center"
          />
        ) : hasCost && !!cost.credits ? (
          <Popup
            trigger={<u>{numberFormat(moneyAsNumber(cost.credits))}</u>}
            content={<ReactJson src={cost} />}
            on="click"
            pinned
            wide="very"
            position="left center"
          />
        ) : (
          <span>n/a</span>
        )}
      </Table.Cell>
      <Table.Cell>
        {ingestion ? (
          <Popup
            size="mini"
            wide="very"
            on="click"
            pinned
            position="left center"
            content={
              <>
                {args.itemInfo.ingested_at && (
                  <FormattedDateWithLabel label="Ingested" dateTime={args.itemInfo.ingested_at} transformTo={DateTimeTransformationTypes.LOCAL} />
                )}
                <div>Extraction ID: {args.itemInfo.ingestion_data_extraction_id || "-"}</div>
                {/* The trackId identifies the Everest row, not this item: usage items
                    that share a subscription line, component and period are merged into
                    one row, so several items can show the same one. It is the handle to
                    quote when asking Everest about a specific charge. Items ingested
                    before the field existed were sent under their own id. */}
                <div>Track ID: {args.itemInfo.ingestion_track_id || item.id || "-"}</div>
                {/* Set while the item is FAILED (Everest's own rejection message or the
                    reason the ingestion worker refused to send it) or SKIPPED (why there
                    was nothing to send: zero usage, zero-rated by the credit plan, ...). */}
                {args.itemInfo.ingestion_error &&
                  (args.itemInfo.ingestion_status === ApiUsageItemIngestionStatus.USAGE_ITEM_INGESTION_STATUS_SKIPPED ? (
                    <div style={{ marginTop: "0.5em", wordBreak: "break-word" }}>
                      <strong>Reason:</strong> {args.itemInfo.ingestion_error}
                    </div>
                  ) : (
                    <div style={{ marginTop: "0.5em", color: "#9f3a38", wordBreak: "break-word" }}>
                      <strong>Error:</strong> {args.itemInfo.ingestion_error}
                    </div>
                  ))}
              </>
            }
            trigger={
              <Label size="small" color={ingestion.color}>
                {ingestion.text}
              </Label>
            }
          />
        ) : (
          "-"
        )}
      </Table.Cell>
      <Table.Cell></Table.Cell>
    </Table.Row>
  );
};

// Interface describing the usage item list
interface IListView extends IWithRefreshProps {
  items: ApiUsageItemInfo[];
  loading: boolean;
  page: number;
  pageSize: number;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onInvoiceSelected: (id: string) => void;
  selectedKind: string;
  onSelectKind: (kind: string) => void;
}

const ListView = ({ ...args }: IListView) => (
  <Table striped>
    <HeaderView {...args} count={args.items.length} />
    <Table.Body>
      {args.items.map((item) => (
        <RowView {...args} key={(item.usage_item || {}).id} itemInfo={item} />
      ))}
    </Table.Body>
  </Table>
);

interface IEmptyView {
  page: number;
}

const EmptyView = ({ ...args }: IEmptyView) => <div>{args.page > 0 ? "No more usage" : "No usage for this organization in selected period"}</div>;

// Interface describing the usage item list view arguments
export interface IUsageItemListViewArgs extends IWithRefreshProps, RouteComponentProps {
  loading: boolean;
  usageItemInfos?: ApiUsageItemInfoList;
  page: number;
  pageSize: number;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  onInvoiceSelected: (id: string) => void;
  selectedKind: string;
  onSelectKind: (kind: string) => void;
}

export const UsageItemListView = ({ ...args }: IUsageItemListViewArgs) => {
  if (!args.usageItemInfos) {
    return <Loading />;
  }
  const isEmpty = !args.usageItemInfos.items || args.usageItemInfos.items.length === 0;
  const usageItems = args.usageItemInfos ? args.usageItemInfos.items : undefined;
  return (
    <div>
      <ListView {...args} items={usageItems || []} />
      {isEmpty && <EmptyView page={args.page} />}
    </div>
  );
};

// Interface decribing the properties of the usage item list component
interface IUsageItemListProps extends IWithRefreshProps, RouteComponentProps {
  organization_id?: string;
  project_id?: string;
  deployment_id?: string;
  onInvoiceSelected: (id: string) => void;
}

// Interface decribing the state of the usage item list component
interface IUsageItemListState {
  usageItemInfos?: ApiUsageItemInfoList;
  page: number;
  pageSize: number;
  from: Date;
  to: Date;
  focusedInput: any;
  organization_id: string;
  project_id: string;
  refreshNeeded: boolean;
  selectedKind: string;
  invoiceID: string;
  deploymentID?: string;
  hideEmpty: boolean;
}

// The component to show usage items of an organization as a list.
class UsageItemList extends Component<IUsageItemListProps, IUsageItemListState> {
  state: IUsageItemListState = {
    usageItemInfos: undefined,
    page: 0,
    pageSize: 40,
    from: momentNow().startOf("day").subtract(2, "days").toDate(),
    to: momentNow().endOf("day").toDate(),
    focusedInput: undefined,
    organization_id: "",
    project_id: "",
    refreshNeeded: false,
    selectedKind: "all",
    invoiceID: "",
    deploymentID: this.props.deployment_id,
    hideEmpty: true,
  };

  reloadUsageItems = async () => {
    const req = buildUsageItemsRequest({
      organization_id: this.props.organization_id,
      project_id: this.props.project_id,
      deploymentID: this.state.deploymentID,
      from: this.state.from,
      to: this.state.to,
      selectedKind: this.state.selectedKind,
      invoiceID: this.state.invoiceID,
      hideEmpty: this.state.hideEmpty,
      page: this.state.page,
      pageSize: this.state.pageSize,
    });
    const usageItemInfos = await apiClients.idashboardClient.ListAllUsageItemInfos(req);
    this.setState({ usageItemInfos: usageItemInfos });
  };

  refreshUsageItems = () => {
    this.props.refreshNow && this.props.refreshNow(this.reloadUsageItems);
  };

  static getDerivedStateFromProps(props: IUsageItemListProps, state: IUsageItemListState) {
    const organization_id = props.organization_id || "";
    const project_id = props.project_id || "";
    if (organization_id != state.organization_id || project_id != state.project_id) {
      return {
        page: 0,
        refreshNeeded: true,
        organization_id: organization_id,
        project_id: project_id,
      };
    }
    // No state update necessary
    return null;
  }

  componentDidMount() {
    this.refreshUsageItems();
  }

  componentDidUpdate() {
    if (this.state.refreshNeeded) {
      this.setState({ refreshNeeded: false }, this.refreshUsageItems);
    }
  }

  onNextPage = () => {
    this.setState(
      (prev) => ({
        page: prev.page + 1,
      }),
      this.refreshUsageItems
    );
  };

  onPreviousPage = () => {
    this.setState(
      (prev) => ({
        page: prev.page - 1,
      }),
      this.refreshUsageItems
    );
  };

  onDatesChange = (args: { startDate: moment.Moment | null; endDate: moment.Moment | null }) => {
    if (args.startDate && args.endDate) {
      this.setState(
        {
          from: args.startDate.toDate(),
          to: args.endDate.toDate(),
          page: 0,
        },
        this.refreshUsageItems
      );
    }
  };

  onInvoiceIDChange = (invoiceID: string) => {
    this.setState({ invoiceID, page: 0 }, this.refreshUsageItems);
  };

  onDeploymentIDChange = (deploymentID: string) => {
    this.setState({ deploymentID, page: 0 }, this.refreshUsageItems);
  };

  onSelectKind = (kind: string) => {
    this.setState({ selectedKind: kind, page: 0 }, this.refreshUsageItems);
  };

  onHideEmptyChange = (hideEmpty: boolean) => {
    this.setState({ hideEmpty, page: 0 }, this.refreshUsageItems);
  };

  render() {
    return (
      <ContentSegment>
        <ErrorMessage active={!!this.props.error} onDismiss={this.props.clearError} message={this.props.error} />
        <UsageItemFilterView
          invoiceID={this.state.invoiceID}
          onInvoiceIDChange={this.onInvoiceIDChange}
          deploymentID={this.state.deploymentID || ""}
          onDeploymentIDChange={this.onDeploymentIDChange}
          fromDate={this.state.from}
          toDate={this.state.to}
          selectedKind={this.state.selectedKind}
          hideEmpty={this.state.hideEmpty}
          onDatesChange={this.onDatesChange}
          onKindChange={this.onSelectKind}
          onHideEmptyChange={this.onHideEmptyChange}
        />
        <UsageCreditsSummary
          organization_id={this.props.organization_id}
          project_id={this.props.project_id}
          deploymentID={this.state.deploymentID}
          from={this.state.from}
          to={this.state.to}
          selectedKind={this.state.selectedKind}
          invoiceID={this.state.invoiceID}
          hideEmpty={this.state.hideEmpty}
        />
        <UsageItemListView
          {...this.props}
          {...this.state}
          onNextPage={this.onNextPage}
          onPreviousPage={this.state.page > 0 ? this.onPreviousPage : undefined}
          onSelectKind={this.onSelectKind}
        />
      </ContentSegment>
    );
  }
}

export default withRefresh()(UsageItemList);
